import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { groupExercisesForSelect } from '../utils/groupExercisesForSelect';
import './comic.css';

const optionStyle = {
  display: "block",
  width: "100%",
  padding: "8px 10px",
  border: "none",
  borderTop: "1px solid #eee",
  background: "none",
  textAlign: "left",
  cursor: "pointer",
  fontFamily: "'Anonymous Pro', monospace",
  fontSize: "14px",
  color: "#232526"
};

const groupLabelStyle = {
  padding: "8px 10px 4px 10px",
  fontFamily: "'Anonymous Pro', monospace",
  fontSize: "11px",
  fontWeight: "bold",
  color: "#232526",
  opacity: 0.6,
  textTransform: "uppercase",
  borderTop: "1px solid #eee"
};

// Searchable exercise picker, replacing a plain native <select> everywhere
// the app asks the user to pick one exercise (Quick Start, "+ Add Set" on a
// past workout, the mid-mission "+ Add Exercise" picker). Behaves like a
// combobox: a text input shows the current selection; focusing it opens a
// dropdown of options (grouped by "muscle group – main muscle", same as the
// native <select>s it replaces, via groupExercisesForSelect); typing
// filters to a flat list of name matches instead. Clicking an option or
// clicking outside closes the dropdown -- clicking outside without picking
// anything reverts the input back to the current `value`, so a half-typed
// search never silently becomes "no selection."
//
// [2026-08-13] The dropdown is rendered via createPortal into document.body
// with `position: fixed` coordinates computed from the input's own
// getBoundingClientRect(), rather than a plain `position: absolute` child --
// caught via live testing, not assumed fine: several call sites (e.g.
// ActiveMission's "+ Add Exercise", nested inside a `.comic-panel--entry`
// card) sit inside an ancestor with `clip-path` (the corner-notch panel
// trick), which clips away any part of an absolutely-positioned descendant
// that overflows the card's own bounds -- the exact same containing-block
// footgun Modal.jsx's own portal fix documents for position:fixed. A window
// scroll/resize listener while open keeps the portaled dropdown's position
// following the real input.
const ExercisePicker = ({ exercises, value, onSelect, placeholder = "Select or Type to Search", inputStyle, wrapperStyle }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || "");
  const [dropdownRect, setDropdownRect] = useState(null);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!open) setQuery(value || "");
  }, [value, open]);

  // Flips the dropdown to open upward, and always clamps its height to
  // whatever space is actually available, so it can never render partly (or
  // entirely) below the viewport -- a real gap caught by testing, not just
  // a cosmetic nicety: since the dropdown is position:fixed, content that
  // extended past the bottom of the screen would be genuinely unreachable
  // (no page scroll brings a fixed element further into view).
  const updateDropdownPosition = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const margin = 8;
    const preferredMaxHeight = 260;
    const spaceBelow = window.innerHeight - rect.bottom - margin;
    const spaceAbove = rect.top - margin;
    const openUpward = spaceBelow < preferredMaxHeight && spaceAbove > spaceBelow;
    const maxHeight = Math.max(100, Math.min(preferredMaxHeight, openUpward ? spaceAbove : spaceBelow));
    setDropdownRect({
      left: rect.left,
      width: rect.width,
      maxHeight,
      ...(openUpward ? { bottom: window.innerHeight - rect.top } : { top: rect.bottom })
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    updateDropdownPosition();
    // Belt-and-suspenders re-measurement, on top of the event listeners
    // below: on a real device, the *very first* input focus of an app
    // session measured wrong here even with a window.visualViewport
    // listener wired up (confirmed live -- that event either fires too late
    // or not at all for whatever's actually settling on that first focus,
    // possibly WKWebView's keyboard/QuickType-bar controller warming up for
    // the first time, not just the viewport resizing). Rather than chase the
    // exact cause further, just re-measure a few times shortly after open on
    // a fixed schedule -- covers the ~250-300ms typical iOS keyboard
    // animation plus margin, self-corrects regardless of what specifically
    // was still settling, and is a no-op (identical position computed
    // twice) on every subsequent open where nothing was actually delayed.
    const retryTimers = [50, 150, 300, 500].map((delay) => setTimeout(updateDropdownPosition, delay));
    window.addEventListener("scroll", updateDropdownPosition, true);
    window.addEventListener("resize", updateDropdownPosition);
    // iOS's on-screen keyboard animating in resizes the visual viewport, but
    // doesn't fire a plain `window.resize` event -- only `window.
    // visualViewport`'s own `resize` (and `scroll`, since the page can also
    // shift to keep the focused input visible above the keyboard).
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", updateDropdownPosition);
      window.visualViewport.addEventListener("scroll", updateDropdownPosition);
    }
    return () => {
      retryTimers.forEach(clearTimeout);
      window.removeEventListener("scroll", updateDropdownPosition, true);
      window.removeEventListener("resize", updateDropdownPosition);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", updateDropdownPosition);
        window.visualViewport.removeEventListener("scroll", updateDropdownPosition);
      }
    };
  }, [open, updateDropdownPosition]);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (e) => {
      const inContainer = containerRef.current && containerRef.current.contains(e.target);
      const inDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
      if (!inContainer && !inDropdown) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const trimmed = query.trim().toLowerCase();
  const isSearching = trimmed.length > 0;
  const filtered = isSearching
    ? exercises.filter((ex) => ex.name.toLowerCase().includes(trimmed))
    : null;
  const groups = groupExercisesForSelect(exercises);

  const handleSelect = (ex) => {
    setQuery(ex.name);
    setOpen(false);
    onSelect(ex);
  };

  return (
    <div ref={containerRef} style={{ position: "relative", ...wrapperStyle }}>
      <input
        type="text"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        className="comic-input"
        style={{ width: "100%", ...inputStyle }}
      />
      {open && dropdownRect && createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: "fixed",
            top: dropdownRect.top,
            bottom: dropdownRect.bottom,
            left: dropdownRect.left,
            width: dropdownRect.width,
            zIndex: 2000,
            maxHeight: `${dropdownRect.maxHeight}px`,
            overflowY: "auto",
            background: "#ffffff",
            border: "1px solid #ccc",
            borderRadius: "8px",
            boxShadow: "0 4px 10px rgba(0,0,0,0.15)"
          }}
        >
          {isSearching ? (
            filtered.length === 0 ? (
              <div style={{ padding: "10px", fontFamily: "'Anonymous Pro', monospace", fontSize: "13px", color: "#232526", opacity: 0.6 }}>
                No exercises match "{query.trim()}".
              </div>
            ) : (
              filtered.map((ex) => (
                <button key={ex.id} type="button" onClick={() => handleSelect(ex)} style={optionStyle}>
                  {ex.name}{ex.mainMuscle ? ` (${ex.mainMuscle})` : ""}
                </button>
              ))
            )
          ) : (
            exercises.length === 0 ? (
              <div style={{ padding: "10px", fontFamily: "'Anonymous Pro', monospace", fontSize: "13px", color: "#232526", opacity: 0.6 }}>
                No exercises available.
              </div>
            ) : (
              groups.map((group) => (
                <div key={group.key}>
                  <div style={groupLabelStyle}>{group.label}</div>
                  {group.list.map((ex) => (
                    <button key={ex.id} type="button" onClick={() => handleSelect(ex)} style={optionStyle}>
                      {ex.name}
                    </button>
                  ))}
                </div>
              ))
            )
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default ExercisePicker;
